import React, { useState, useMemo } from 'react';
import { ExternalLink, Copy, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '../common/StatusBadge';

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  if (!text) return null;
  const handleCopy = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(text ?? '');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handleCopy} className="shrink-0 text-gray-400 hover:text-blue-600 transition-colors" title="Copy URL">
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

const ensureHttps = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

export const DCUsTable = ({ data, tableHeight = '750px' }) => {
  const navigate = useNavigate();
  const columns = [
    { key: 'partner', label: 'PARTNER', width: '160px' },
    { key: 'url', label: 'DCU', width: '150px' },
    { key: 'ppf_url', label: 'PPU', width: '150px' },
    { key: 'ppf_checksum', label: 'POLICY', width: '100px' },
    { key: 'country_code', label: 'COUNTRY', width: '100px' },
    { key: 'is_url_active', label: 'CONNECTION STATUS', width: '150px' },
    { key: 'consumer_counts', label: 'CONSUMER RECORDS', width: '130px' },
    { key: 'last_seen_at', label: 'LATEST RECORD', width: '100px' }
  ];

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key] ?? '';
      const bValue = b[sortConfig.key] ?? '';
      if (typeof aValue === 'string') {
        return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      } else {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
    });
  }, [data, sortConfig]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dateStr.split("T")[0];
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return (
    <div
      className="overflow-y-auto border rounded-lg"
      style={{ maxHeight: tableHeight }}
    >
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0 z-10">
          <tr className="border-b border-gray-200">
            {columns.map((col) => (
              <th
                key={col.key}
                className="relative text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-2 border-r border-gray-200 last:border-r-0"
                style={{ width: col.width }}
              >
                {col.label}
                <svg className="absolute bottom-1 right-1 opacity-70" width="12" height="12" viewBox="0 0 12 12">
                  <line x1="11" y1="1" x2="1" y2="11" stroke="#374151" strokeWidth="1.5"/>
                  <line x1="11" y1="5" x2="5" y2="11" stroke="#374151" strokeWidth="1.5"/>
                  <line x1="11" y1="9" x2="9" y2="11" stroke="#374151" strokeWidth="1.5"/>
                </svg>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedData.map((row) => (
            <tr key={row.id} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-4 px-2 text-gray-700" style={{ width: '160px' }}>{row.partner}</td>

              <td className="py-4 px-2" style={{ width: '150px' }}>
                <div className="flex items-center gap-1 max-w-[150px]">
                  <a href={ensureHttps(row.url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate" title={row.url}>
                    {row.url}
                  </a>
                  <CopyButton text={row.url} />
                </div>
              </td>

              <td className="py-4 px-2" style={{ width: '150px' }}>
                <div className="flex items-center gap-1 max-w-[150px]">
                  <a href={ensureHttps(row.ppf_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate" title={row.ppf_url}>
                    {row.ppf_url}
                  </a>
                  <CopyButton text={row.ppf_url} />
                </div>
              </td>

              <td className="py-4 px-2 max-w-[180px] truncate" style={{ width: '180px' }}>
                {row.ppf_checksum ? (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => navigate(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}&ppf_file=${encodeURIComponent(row.ppf_file_name)}`)}
                      className="text-blue-600 hover:underline text-sm text-left truncate"
                      title={row.ppf_file_name}
                    >
                      {row.ppf_file_name}
                    </button>
                    <button
                      onClick={() => window.open(`/policy?policy=${encodeURIComponent(row.ppf_checksum)}&ppf_file=${encodeURIComponent(row.ppf_file_name)}`, '_blank')}
                      className="text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <span className="text-gray-400 text-sm">—</span>
                )}
              </td>

              <td className="py-4 px-2 text-gray-700" style={{ width: '100px' }}>{row.country_code}</td>

              <td className="py-4 px-2" style={{ width: '150px' }}>
                <StatusBadge 
                  status={row.is_url_active} 
                  subtext={formatDate(row.scraper_date)}
                />
              </td>

              <td className="py-4 px-2 text-gray-700" style={{ width: '150px' }}>
                {row.consumer_counts?.toLocaleString() || '0'}
              </td>

              <td className="py-4 px-2 text-gray-700 text-sm" style={{ width: '100px' }}>
                {formatDate(row.last_seen_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
